"use client";

import { cn } from "@/lib/utils";
import { useChat } from "@/providers/chat-provider";
import { useStore } from "@/providers/global-store-provider";
import { ExternalLinkIcon, XIcon } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import Chat from "../chat";
import CommunityChatsTab from "../chat-tabs/community-chats-tab";
import DirectChatsTab from "../chat-tabs/direct-chats-tab";
import Image from "next/image";

const tabs = [
	{ id: "group", name: "Community chat" },
	{ id: "direct", name: "Direct messages" },
];

const ChatWidget = () => {
	const {
		activeChat,
		activeTab,
		directChats,
		showListOfChatsInInbox,
		handleCloseChatWidget,
		handleTabChange,
	} = useChat();

	const { numberOfUnreadChats: defaultNumberOfUnreadChats } = useStore();
	const [numberOfUnreadChats, setNumberOfUnreadChats] = useState(0);

	const [showChat, setShowChat] = useState<boolean>(false);

	const onTabChange = (tabId: typeof activeTab) => {
		handleTabChange(tabId);
	};

	useEffect(() => {
		if (activeChat && !showListOfChatsInInbox) {
			setShowChat(true);
		} else {
			setShowChat(false);
		}
	}, [activeChat, showListOfChatsInInbox]);

	useEffect(() => {
		if (activeChat?.type === "direct") {
			setNumberOfUnreadChats(0);
		}
	}, [activeChat, defaultNumberOfUnreadChats]);

	return (
		<div className="bg-white shadow-lg md:rounded-l-2xl">
			<div className="mb-2 flex items-center justify-between gap-2 p-3">
				<Image
					src="/images/proptechbuzz-logo.svg"
					alt="PropTechBuzz Logo"
					width={44}
					height={44}
					className="h-6 w-44 fill-[#271B58]"
				/>

				<div className="flex items-center gap-4">
					<Link href="/chat" target="_blank" rel="noreferrer">
						<ExternalLinkIcon className="h-4 w-4" />
					</Link>

					<button type="button" onClick={handleCloseChatWidget}>
						<XIcon className="h-5 w-5" />
					</button>
				</div>
			</div>

			<div className="mt-2 flex h-[calc(100vh-4rem)] w-full flex-col overflow-hidden md:w-72 lg:w-80">
				<div className="flex items-center justify-evenly gap-2 rounded-lg bg-gray-50 px-4 py-1.5">
					{tabs.map((tab) => (
						<button
							key={tab.id}
							className={cn(
								"flex w-full items-center justify-center gap-1 rounded-lg py-2.5 text-sm font-semibold outline-none disabled:cursor-not-allowed disabled:opacity-60",
								activeTab === tab.id
									? "bg-white text-gray-700 shadow"
									: "bg-transparent text-gray-500"
							)}
							onClick={() => onTabChange(tab.id as typeof activeTab)}
							disabled={tab.id === "direct" && directChats.length === 0}
						>
							{tab.name}

							{tab.id === "direct" && numberOfUnreadChats > 0 ? (
								<div className="flex h-6 w-6 items-center justify-center rounded-full bg-red-500 p-1 text-xxs font-bold text-white">
									{numberOfUnreadChats}
								</div>
							) : null}
						</button>
					))}
				</div>

				{showChat ? (
					<Chat chatId={activeChat?.uniqueChatId} />
				) : showListOfChatsInInbox && activeTab === "direct" ? (
					<DirectChatsTab />
				) : (
					<CommunityChatsTab />
				)}
			</div>
		</div>
	);
};

export default ChatWidget;
